import { useEffect, useState } from 'react';
import { TeContainer, TeText } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { IconName } from '@topcon/tuicl/dist/components/icon/icon.utils';
import { PlaceholderContainer, useAuthentication } from '@topcon/topcon-utils';

import { Section } from '../../../utils/styling';
import {
  type ChannelMap,
  type ChannelList,
  type Taxon,
} from '../../../api/generated/notification-pref';
import NotificationTypeCard from './NotificationTypeCard';
import NotificationSettingsTopicsSection from './NotificationSettingsTopicsSection';
import useNotificationSettings from '../../../hooks/useNotificationSettings';
import { findChannelsForNotificationClass } from '../../../utils/notifications';
import Header from '../../../components/Header';

type Channels = ChannelList[number];
const NotificationSettings = ({ aspect = 'user' }: { aspect?: 'user' | 'identity' }) => {
  const {
    isLoadingSubscriptions,
    isLoadingTaxonomy,
    subscriptionsData,
    handleUpdateChannel,
    taxonomyData,
    isUpdatingPreference,
    isError,
  } = useNotificationSettings(aspect);
  const hasCachedData = !!taxonomyData && !!subscriptionsData;
  const { t } = useTranslation();
  const auth = useAuthentication();
  const [channels, setChannels] = useState<ChannelMap>(
    typeof subscriptionsData?.channels === 'object' && subscriptionsData?.channels !== null
      ? (Object.fromEntries(
          Object.entries(subscriptionsData.channels)
            .filter(([, v]) => typeof v === 'boolean')
            .map(([k, v]) => [k, v as boolean])
        ) as ChannelMap)
      : {}
  );

  useEffect(() => {
    if (subscriptionsData?.channels && typeof subscriptionsData.channels === 'object') {
      const newChannels = Object.entries(subscriptionsData.channels).reduce((acc, [key, value]) => {
        if (typeof value === 'boolean') {
          acc[key as keyof ChannelMap] = value;
        }
        return acc;
      }, {} as ChannelMap);
      setChannels(newChannels);
    }
  }, [subscriptionsData?.channels]);

  const notificationChannelLabels: Partial<
    Record<
      Channels,
      {
        label: string;
        sublabel?: string;
        iconName: IconName;
        disabled?: boolean;
        iconOutline?: boolean;
      }
    >
  > = {
    ['in_app']: {
      label: t('notifications.in_app.title'),
      iconName: 'public',
    },
    ['email']: {
      label: t('notifications.email.title'),
      sublabel: t('notifications.email.description', { email: auth.username }),
      iconName: 'email',
    },
    // SMS channel is currently not supported and will be added at a later time
    // ['sms']: {
    //   label: t('notifications.sms.title'),
    //   sublabel: t('notifications.sms.description'),
    //   iconName: 'phone_iphone',
    //   iconOutline: true,
    //   disabled: true,
    // },
  };

  const handleChangeSubscriptionClass = (className: string, value: boolean) => {
    if (!taxonomyData) return;
    // Search through all root classes in the taxonomy
    // The className is the full identifier (e.g., "ciam.domain.created")
    let notificationClassChannels: string[] | undefined;
    for (const rootClass of Object.values(taxonomyData.classes || {})) {
      notificationClassChannels = findChannelsForNotificationClass(rootClass as Taxon, className);
      if (notificationClassChannels) break;
    }
    if (!notificationClassChannels) return;
    const channelsToValueObj: ChannelMap = notificationClassChannels.reduce(
      (result, channel) => ({ ...result, [channel]: value }),
      {} as ChannelMap
    );
    handleUpdateChannel({
      subscriptions: {
        channels: typeof subscriptionsData?.channels === 'object' ? channels : {},
        classes: {
          ...(subscriptionsData?.classes ?? {}),
          [className]: {
            channels: channelsToValueObj,
          },
        },
      },
    });
  };

  const handleChangeSubscriptionChannel = (channel: string, value: boolean) => {
    setChannels((prevChannels) => ({
      ...prevChannels,
      [channel]: value,
    }));
    handleUpdateChannel({
      subscriptions: {
        channels: { ...channels, [channel]: value } as ChannelMap,
        classes: subscriptionsData?.classes ?? {},
      },
    });
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('notifications.settings.title')} />
      {isError && !hasCachedData ? (
        <Section $padding="16px">
          <TeText>{t('notifications.settings.loadError')}</TeText>
        </Section>
      ) : isLoadingTaxonomy || isLoadingSubscriptions ? (
        <NotificationsSettingsLoading />
      ) : (
        <Section $background="transparent">
          <TeContainer column backgroundColor="background.primary" fill gap="s" padding="m">
            {isError && <TeText>{t('notifications.settings.loadError')}</TeText>}
            <TeText weight="bold" size="l">
              {t('notifications.settings.notifyMeVia')}
            </TeText>
            {Object.entries(notificationChannelLabels).map(([channel, obj]) => (
              <NotificationTypeCard
                label={obj.label}
                sublabel={obj.sublabel}
                iconName={obj.iconName}
                iconOutline={obj.iconOutline}
                disabled={obj.disabled || isUpdatingPreference}
                checked={channels[channel as Channels] !== false}
                key={channel}
                onCheckedChange={(value) =>
                  handleChangeSubscriptionChannel(channel as Channels, value)
                }
              />
            ))}
          </TeContainer>

          <NotificationSettingsTopicsSection
            taxonomyData={taxonomyData}
            subscriptionsData={subscriptionsData}
            onCheckedChange={handleChangeSubscriptionClass}
            disabled={isUpdatingPreference}
          />
        </Section>
      )}
    </TeContainer>
  );
};

const NotificationsSettingsLoading = () => {
  const { t } = useTranslation();
  return (
    <Section $padding="16px">
      <PlaceholderContainer
        borderRadius={4}
        grow
        backgroundColor="background.secondary"
        hasLoader
        text={t('notifications.loadingSettings')}
      />
    </Section>
  );
};

export default NotificationSettings;
